import React, { useState, useMemo, useRef, useEffect } from "react";
import {
  useMaterialReactTable,
  MaterialReactTable,
} from "material-react-table";
import { Container, Button } from "react-bootstrap";
import ClaimsTableFilters from "./ClaimsTableFilters";
import Loader from "../../components/loader/Loader";
import UnassignedClaimModal from "./UnassignedClaimModal";
import "../common/tableStyles.css";
import { LuSearch, LuPen } from "react-icons/lu";
import { MdViewList, MdViewModule } from "react-icons/md";
import Chip from "@mui/material/Chip";
import { COMMON_STYLES, TABLE_STYLES, StyledPagination } from "./tableStyles";
import { handleClaimDetails } from "../../services/claims";
import { useStyles } from "../common/styles";
import TableContainer from "@mui/material/TableContainer";
import { handleCookies } from "../../utils/cookies";
import {
  ToggleButton,
  ToggleButtonGroup,
  Card,
  CardContent,
  Grid,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

function ClaimsTableWrapper() {
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [claimsData, setClaimsData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [addModalStatus, setAddModalStatus] = useState(false);
  const [modalInfo, setModalInfo] = useState(false);
  const searchRef = useRef(null);
  const [selectedFilterValue, setSelectedFilterValue] = useState(0);
  const [globalFilter, setGlobalFilter] = useState("");
  const [selectedClaim, setSelectedClaim] = useState({});
  const [viewMode, setViewMode] = useState("table"); // 'table' or 'cards'

  const currentUserData = handleCookies.fetchCookies();
  const classes = useStyles();
  const debouncedSearchValue = useDebounce(globalFilter, 100);

  useEffect(() => {
    getClaimsData(selectedFilterValue, debouncedSearchValue);
  }, [
    debouncedSearchValue,
    pagination.pageIndex,
    pagination.pageSize,
    selectedFilterValue,
  ]);

  const handleClaimClick = (rowData) => {
    setSelectedClaim(rowData);
    setAddModalStatus(true);
    setModalInfo(true);
  };

  const handleClose = () => {
    setSelectedClaim({});
    setAddModalStatus(false);
  };

  const onUpdate = async () => {
    getClaimsData(selectedFilterValue, debouncedSearchValue);
  };

  useEffect(() => {
    if (searchRef.current) {
      clearSearchField();
    }
  }, [searchRef.current]);

  const clearSearchField = () => {
    if (searchRef.current) {
      const inputElement = searchRef.current.querySelector(
        ".MuiInputBase-input",
      );
      if (inputElement) {
        inputElement.value = "";
        searchRef.current.value = "";
      }
    }
  };

  const handleFilterSelect = (value) => {
    setSelectedFilterValue(value);
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
    getClaimsData(value);
  };

  const handleViewModeChange = (event, newViewMode) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  const handlePageSizeChange = (event) => {
    setPagination((prev) => ({
      ...prev,
      pageSize: parseInt(event.target.value, 10),
      pageIndex: 0, // Reset to first page when changing page size
    }));
  };

  const CustomTableToolbar = () => (
    <div className="d-flex justify-content-end w-100">
      <div className="table-header search-filter-gap">
        <ClaimsTableFilters
          handleFilterSelect={handleFilterSelect}
          selectedFilterValue={selectedFilterValue}
          loading={loading}
        />
      </div>
    </div>
  );

  const claimColumns = useMemo(
    () => [
      {
        accessorKey: "claim_number",
        header: "Claim Number",
        size: 100,
      },
      {
        accessorFn: (row) => row.claim_status,
        header: "Status",
        muiTableBodyCellProps: {
          sx: {
            flex: "0 0 90px !important",
            minWidth: "90px",
            maxWidth: "90px",
            fontFamily: COMMON_STYLES.fonts.inter,
            color: COMMON_STYLES.colors.text.secondary,
            fontSize: "10px",
          },
        },
        muiTableHeadCellProps: {
          sx: {
            flex: "0 0 90px !important",
            minWidth: "90px",
            maxWidth: "90px",
            fontFamily: "Inter",
            fontWeight: 700,
            fontSize: "10px",
            lineHeight: "19px",
            letterSpacing: "0.01071em",
            display: "flex",
            verticalAlign: "inherit",
            borderBottom: "none",
            textAlign: "left",
            color: "#9AA4B2",
            backgroundColor: "inherit",
            backgroundImage: "inherit",
            textTransform: "uppercase",
          },
        },
        Cell: ({ cell }) => {
          const value = cell.getValue();
          const getStatusDetails = (status) => {
            switch (status) {
              case "OP":
                return { label: "Open", color: "#175CD3", bgColor: "#EFF8FF" };
              case "CL":
                return {
                  label: "Closed",
                  color: "#027A48",
                  bgColor: "#ECFDF3",
                };
              default:
                return { label: status, color: "#B54708", bgColor: "#FFFAEB" };
            }
          };

          const { label, color, bgColor } = getStatusDetails(value);
          return (
            <Chip
              label={label}
              size="small"
              sx={{
                backgroundColor: bgColor,
                color: color,
                margin: "0px",
                padding: "0px",
                height: "auto",
                "& .MuiChip-label": {
                  padding: "2px 8px",
                },
              }}
            />
          );
        },
      },
      {
        accessorFn: (row) => row.insured_defendant_name || "-",
        header: "Insured/Defendant Name",
        size: 225,
      },
      {
        accessorFn: (row) => row.policy_number || "-",
        header: "Policy #",
        size: 100,
      },
      {
        accessorFn: (row) => row.plaintiff_attorny || "-",
        header: "Plaintiff Attorney",
        size: 75,
      },
      {
        accessorFn: (row) => row.plaintiff_law_firm || "-",
        header: "Plaintiff Law Firm",
        size: 75,
      },
      {
        accessorFn: (row) => row.claimant_name || "-",
        header: "Claimant Name",
        size: 150,
      },
      {
        accessorFn: (row) => row.id,
        id: "id",
        size: 1,
        align: "right",
        muiTableBodyCellProps: {
          sx: {
            flex: "0 0 50px !important",
            minWidth: "50px",
            maxWidth: "50px",
            padding: "0px !important",
            margin: "0px !important",
            gap: "0px !important",
          },
        },
        muiTableHeadCellProps: {
          sx: {
            flex: "0 0 50px !important",
            minWidth: "50px",
            maxWidth: "50px",
            padding: "0px !important",
            margin: "0px !important",
            gap: "0px !important",
            borderBottom: "none",
          },
        },
        Cell: ({ cell }) => {
          return (
            <>
              <Button
                variant=""
                size="sm"
                onClick={() => handleClaimClick(cell.row.original)}
              >
                <LuPen />
              </Button>
            </>
          );
        },
      },
    ],
    [],
  );

  const getClaimsData = async (
    filterValue = selectedFilterValue,
    searchValue = debouncedSearchValue,
  ) => {
    setLoading(true);
    try {
      const response = await handleClaimDetails.getUnassignedClaims({
        offset: pagination.pageIndex * pagination.pageSize,
        limit: pagination.pageSize,
        claim_status: filterValue !== "0" ? filterValue : null,
        search: searchValue || null,
      });
      setClaimsData(response.data.claims_data);
      setTotalCount(response.data.pagination.total);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching claims:", error);
      setClaimsData([]);
      setTotalCount(0);
      setLoading(false);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: newPage - 1,
    }));
  };

  const getStatusDetails = (status) => {
    switch (status) {
      case "OP":
        return { label: "Open", color: "#175CD3", bgColor: "#EFF8FF" };
      case "CL":
        return { label: "Closed", color: "#027A48", bgColor: "#ECFDF3" };
      default:
        return { label: status, color: "#B54708", bgColor: "#FFFAEB" };
    }
  };

  const ClaimCard = ({ claim }) => {
    const statusDetails = getStatusDetails(claim.claim_status);

    return (
      <Card
        sx={{
          borderRadius: "12px",
          border: "1px solid #EAECF0",
          boxShadow: "0px 1px 2px 0px #1018280F, 0px 1px 3px 0px #1018281A",
          cursor: "pointer",
          transition: "all 0.2s",
          "&:hover": {
            backgroundColor: "#F9FAFB",
            boxShadow: "0px 4px 8px 0px #1018281A",
          },
        }}
        onClick={() => handleClaimClick(claim)}
      >
        <CardContent sx={{ padding: "20px !important" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 2,
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "Inter",
                  fontSize: "16px",
                  fontWeight: 600,
                  color: "#101828",
                  mb: 0.5,
                }}
              >
                {claim.claim_number}
              </Typography>
              <Chip
                label={statusDetails.label}
                size="small"
                sx={{
                  backgroundColor: statusDetails.bgColor,
                  color: statusDetails.color,
                  height: "auto",
                  "& .MuiChip-label": {
                    padding: "2px 8px",
                    fontSize: "12px",
                    fontWeight: 500,
                  },
                }}
              />
            </Box>
            <Button
              variant=""
              size="sm"
              onClick={(event) => {
                event.stopPropagation();
                handleClaimClick(claim);
              }}
              style={{ padding: "4px 8px" }}
            >
              <LuPen />
            </Button>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 1.5 }}>
                <Typography
                  sx={{
                    fontFamily: "Inter",
                    fontSize: "10px",
                    fontWeight: 700,
                    color: "#9AA4B2",
                    textTransform: "uppercase",
                    mb: 0.5,
                  }}
                >
                  Insured/Defendant Name
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Inter",
                    fontSize: "14px",
                    color: "#475467",
                    wordBreak: "break-word",
                  }}
                >
                  {claim.insured_defendant_name || "-"}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 1.5 }}>
                <Typography
                  sx={{
                    fontFamily: "Inter",
                    fontSize: "10px",
                    fontWeight: 700,
                    color: "#9AA4B2",
                    textTransform: "uppercase",
                    mb: 0.5,
                  }}
                >
                  Policy #
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Inter",
                    fontSize: "14px",
                    color: "#475467",
                    wordBreak: "break-word",
                  }}
                >
                  {claim.policy_number || "-"}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 1.5 }}>
                <Typography
                  sx={{
                    fontFamily: "Inter",
                    fontSize: "10px",
                    fontWeight: 700,
                    color: "#9AA4B2",
                    textTransform: "uppercase",
                    mb: 0.5,
                  }}
                >
                  Plaintiff Attorney
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Inter",
                    fontSize: "14px",
                    color: "#475467",
                    wordBreak: "break-word",
                  }}
                >
                  {claim.plaintiff_attorny || "-"}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 1.5 }}>
                <Typography
                  sx={{
                    fontFamily: "Inter",
                    fontSize: "10px",
                    fontWeight: 700,
                    color: "#9AA4B2",
                    textTransform: "uppercase",
                    mb: 0.5,
                  }}
                >
                  Plaintiff Law Firm
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Inter",
                    fontSize: "14px",
                    color: "#475467",
                    wordBreak: "break-word",
                  }}
                >
                  {claim.plaintiff_law_firm || "-"}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 1.5 }}>
                <Typography
                  sx={{
                    fontFamily: "Inter",
                    fontSize: "10px",
                    fontWeight: 700,
                    color: "#9AA4B2",
                    textTransform: "uppercase",
                    mb: 0.5,
                  }}
                >
                  Claimant Name
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Inter",
                    fontSize: "14px",
                    color: "#475467",
                    wordBreak: "break-word",
                  }}
                >
                  {claim.claimant_name || "-"}
                </Typography>
              </Box>
            </Grid>

            {/* Fraudulent field - Only visible to Admin */}
            {currentUserData?.user_role === "Admin" && (
              <Grid item xs={12} sm={6}>
                <Box sx={{ mb: 1.5 }}>
                  <Typography
                    sx={{
                      fontFamily: "Inter",
                      fontSize: "10px",
                      fontWeight: 700,
                      color: "#9AA4B2",
                      textTransform: "uppercase",
                      mb: 0.5,
                    }}
                  >
                    Fraudulent
                  </Typography>
                  <Chip
                    label={claim.is_fraudulent ? "Yes" : "No"}
                    size="small"
                    sx={{
                      backgroundColor: claim.is_fraudulent
                        ? "#FEE4E2"
                        : "#ECFDF3",
                      color: claim.is_fraudulent ? "#B42318" : "#027A48",
                      height: "auto",
                      "& .MuiChip-label": {
                        padding: "2px 8px",
                        fontSize: "12px",
                        fontWeight: 500,
                      },
                    }}
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>
    );
  };

  const customTable = useMaterialReactTable({
    columns: claimColumns,
    enableRowsPerPage: false,
    muiTableContainerProps: { sx: { tableLayout: "fixed" } },
    displayRowsPerPage: false,
    data: claimsData,
    rowCount: totalCount,
    enablePagination: true,
    manualPagination: true,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableRowSelection: false,
    enableSorting: false,
    onPaginationChange: setPagination,
    paginationDisplayMode: "pagination",
    state: {
      pagination,
      globalFilter,
      isLoading: loading,
    },
    enableGlobalFilter: true,
    manualFiltering: true,
    onGlobalFilterChange: setGlobalFilter,
    getRowId: (row) => row.id,
    muiTablePaperProps: TABLE_STYLES.paper,
    initialState: { showGlobalFilter: true },
    muiSearchTextFieldProps: {
      ...TABLE_STYLES.searchField,
      className: classes.searchField,
      InputProps: {
        disabled: loading,
        startAdornment: (
          <LuSearch
            style={{
              color: COMMON_STYLES.colors.text.secondary,
              fontSize: "20px",
              marginRight: "10px",
            }}
          />
        ),
      },
    },
    muiTableHeadCellProps: TABLE_STYLES.headCell,
    muiTableHeadRowProps: TABLE_STYLES.headRow,
    muiTopToolbarProps: TABLE_STYLES.topToolbar,
    muiTableBodyProps: TABLE_STYLES.tableBody,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => handleClaimClick(row.original),
      sx: TABLE_STYLES.tableBodyRow.sx,
    }),
    muiPaginationProps: {
      showRowsPerPage: false, // This disables rows per page
      showFirstButton: false,
      showLastButton: false,
      rowsPerPageOptions: [], // This removes the options
    },
    renderTopToolbarCustomActions: () => <CustomTableToolbar />,
  });

  return (
    <>
      {/* <Loader open={open} /> */}
      <Container>
        <div className="d-flex align-items-center">
          <h4 className="m-0 p-0">Insurance Claim</h4>
        </div>

        {selectedClaim && Object.keys(selectedClaim).length > 0 && (
          <UnassignedClaimModal
            handleClose={handleClose}
            onUpdate={onUpdate}
            addModalStatus={addModalStatus}
            modalInfo={modalInfo}
            claimData={selectedClaim}
          />
        )}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            mt: 3,
          }}
        >
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              aria-label="view mode"
              size="small"
              disabled={loading}
              sx={{
                "& .MuiToggleButton-root": {
                  border: "1px solid #EAECF0",
                  color: "#475467",
                  padding: "8px 12px",
                  "&.Mui-selected": {
                    backgroundColor: "#099250",
                    color: "#FFFFFF",
                    "&:hover": {
                      backgroundColor: "#087A42",
                    },
                  },
                  "&:hover": {
                    backgroundColor: "#F9FAFB",
                  },
                },
              }}
            >
              <ToggleButton value="table" aria-label="table view">
                <MdViewList style={{ fontSize: "20px", marginRight: "4px" }} />
                Table
              </ToggleButton>
              <ToggleButton value="cards" aria-label="card view">
                <MdViewModule
                  style={{ fontSize: "20px", marginRight: "4px" }}
                />
                Cards
              </ToggleButton>
            </ToggleButtonGroup>

            {/* Records Per Page Selector */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontSize: "14px",
                  color: "#475467",
                  whiteSpace: "nowrap",
                }}
                disabled={loading}
              >
                Show:
              </Typography>
              <select
                value={pagination.pageSize}
                onChange={handlePageSizeChange}
                disabled={loading}
                style={{
                  padding: "8px 12px",
                  border: "1px solid #EAECF0",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontFamily: "Inter",
                  color: "#475467",
                  backgroundColor: "#FFFFFF",
                  cursor: "pointer",
                  outline: "none",
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </Box>
          </Box>
        </Box>

        <div className="tab-content">
          {viewMode === "table" ? (
            <div>
              <MaterialReactTable
                table={customTable}
                components={{
                  Container: (props) => (
                    <TableContainer sx={{ tableLayout: "fixed" }} {...props} />
                  ),
                }}
              />
            </div>
          ) : loading ? (
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "200px",
                }}
              >
                <CircularProgress />
              </Box>
            </Grid>
          ) : (
            <Grid container spacing={3} sx={{ mb: 8 }}>
              {claimsData.map((claim) => (
                <Grid item xs={12} sm={6} md={4} key={claim.id}>
                  <ClaimCard claim={claim} />
                </Grid>
              ))}
            </Grid>
          )}
          {!loading && (
            <StyledPagination
              page={pagination.pageIndex + 1}
              count={Math.ceil(totalCount / pagination.pageSize)}
              onChange={handlePageChange}
            />
          )}

          {!loading && (
            <div
              style={{
                fontSize: "14px",
                color: "#475467",
              }}
            >
              <div>
                Showing{" "}
                {totalCount === 0
                  ? 0
                  : pagination.pageIndex * pagination.pageSize + 1}{" "}
                -{" "}
                {Math.min(
                  (pagination.pageIndex + 1) * pagination.pageSize,
                  totalCount,
                )}{" "}
                of {totalCount} records
              </div>
            </div>
          )}
        </div>
      </Container>
    </>
  );
}

export default ClaimsTableWrapper;
